import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaChair, FaPlus, FaSyncAlt } from 'react-icons/fa';
import { getAllStudentsData, getLibraries, getSeatsData } from '../services/api.js';
import { getSeatTypes } from '../services/seatTypes.js';
import { useToast } from '../context/ToastContext.jsx';
import { SeatsDirectory } from './SeatsDirectory.jsx';
import '../styles/pages/seats.css';

export function SeatsPage({ selectedLibrary = 'All Libraries' }) {
  const [seats, setSeats] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [students, setStudents] = useState([]);
  const [seatTypes, setSeatTypes] = useState({ status: [], category: [] });
  const [loading, setLoading] = useState(true);
  const directoryRef = useRef(null);
  const { showToast } = useToast();

  const fetchAll = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const [seatData, libraryData, studentData, types] = await Promise.all([
        getSeatsData(forceRefresh),
        getLibraries(),
        getAllStudentsData(null, null, forceRefresh),
        getSeatTypes(),
      ]);
      const seatList = Array.isArray(seatData) ? seatData : (seatData?.seats || []);
      const roomList = Array.isArray(seatData) ? [] : (seatData?.rooms || []);
      const studentList = Array.isArray(studentData) ? studentData : (studentData?.students || []);
      setSeats(Array.isArray(seatList) ? seatList : []);
      setRooms(Array.isArray(roomList) ? roomList : []);
      setLibraries(Array.isArray(libraryData) ? libraryData : []);
      setStudents(Array.isArray(studentList) ? studentList : []);
      setSeatTypes(types || { status: [], category: [] });
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Could not load seats', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading && !seats.length) {
    return (
      <>
        <header className="dash-header seats-page-header">
          <div className="dash-header-bar">
            <div className="seats-header-left">
              <div className="seats-header-icon" aria-hidden="true"><FaChair /></div>
              <div className="dash-header-info seats-header-info">
                <div className="seats-heading-copy">
                  <h1 className="dash-header-title">Seat Management</h1>
                  <p className="dash-header-subtitle">View, assign, reserve, and release seats across every room.</p>
                </div>
                <div className="dash-header-actions">
                  <button type="button" className="dash-action-btn" disabled aria-label="Refresh seats">
                    <span className="dash-action-icon"><FaSyncAlt aria-hidden="true" /></span><span>Refresh</span>
                  </button>
                  <button type="button" className="dash-action-btn primary" disabled>
                    <FaPlus aria-hidden="true" /><span>Add New Seat</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>
        <section className="panel seats-filter-card" aria-label="Seat filters">
          <div className="seats-filter-head">
            <div className="seats-filter-title">
              <span className="seats-filter-icon" aria-hidden="true"><FaChair /></span>
              <div>
                <b>Seats Directory</b>
                <small>Filter and manage seats by library, room, status, or category.</small>
              </div>
            </div>
            <div className="skeleton-text short" />
          </div>
          <div className="seats-filter-controls">
            <label className="search-wrap seats-search">
              <input placeholder="Search by seat number or student name..." disabled />
            </label>
            <div className="skeleton-select" />
            <div className="skeleton-select" />
            <div className="skeleton-select" />
            <div className="skeleton-select" />
            <button type="button" className="button ghost seats-filter-clear" disabled>
              <span>Clear filters</span>
            </button>
          </div>
        </section>
        <section className="panel seats-table-panel">
          <div className="seats-table-wrap">
            <table className="seats-table">
              <thead>
                <tr>
                  <th>Seat</th>
                  <th>Room</th>
                  <th>Library</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Student</th>
                  <th>Subscription End</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                  <tr key={item}>
                    <td><div className="skeleton-text short" /></td>
                    <td><div className="skeleton-text" /></td>
                    <td><div className="skeleton-text" /></td>
                    <td><div className="skeleton-pill" /></td>
                    <td><div className="skeleton-pill" /></td>
                    <td><div className="skeleton-text" /></td>
                    <td><div className="skeleton-text short" /></td>
                    <td><div className="skeleton-text short" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <header className="dash-header seats-page-header">
        <div className="dash-header-bar">
          <div className="seats-header-left">
            <div className="seats-header-icon" aria-hidden="true"><FaChair /></div>
            <div className="dash-header-info seats-header-info">
              <div className="seats-heading-copy">
                <h1 className="dash-header-title">Seat Management</h1>
                <p className="dash-header-subtitle">View, assign, reserve, and release seats across every room.</p>
              </div>
              <div className="dash-header-actions">
                <button type="button" className="dash-action-btn" onClick={() => fetchAll(true)} disabled={loading} aria-label="Refresh seats">
                  <span className={`dash-action-icon ${loading ? 'is-loading' : ''}`}><FaSyncAlt aria-hidden="true" /></span><span>{loading ? 'Refreshing...' : 'Refresh'}</span>
                </button>
                <button type="button" className="dash-action-btn primary" onClick={() => directoryRef.current?.openAdd()}>
                  <FaPlus aria-hidden="true" /><span>Add New Seat</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      <SeatsDirectory
        ref={directoryRef}
        seats={seats}
        rooms={rooms}
        libraries={libraries}
        students={students}
        seatTypes={seatTypes}
        onRefresh={() => fetchAll(true)}
        selectedLibrary={selectedLibrary}
      />
    </>
  );
}

export default SeatsPage;
